// Runs after index.js
import logger from '@eventbrite/client-logger';
import {
    App,
    ConditionalStatsigSynchronousProvider,
    DEFAULT_QUERYCLIENT_OPTIONS,
    rumStatsigEvaluationCallback,
    ServerProps,
    STATSIG_SERVER_BOOTSTRAP_CLIENT_KEY,
    TrackingContext,
} from '@eventbrite/discover-lib';
import { SentryErrorBoundary } from '@eventbrite/error-boundary';
import { FeatureFlagsProvider } from '@eventbrite/feature-flag-utils';
import {
    getLocaleOverride,
    getParentLocale,
    gettext,
    setupAsync,
} from '@eventbrite/i18n';
import { initializeSdk } from '@eventbrite/statsig';
import { loadableReady } from '@loadable/component';
import {
    dehydrate,
    Hydrate,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { createHistory } from 'history';
import React from 'react';
import { hydrate } from 'react-dom';

// retrieve data from server to hydrate the client
const props: ServerProps = window.__SERVER_DATA__ || {};
const featureFlags = props.featureFlags;
const eventCreationFeatureFlags = props.eventCreationFeatureFlags;

// retrieve dehydrated react-query state to hydrate
const queryClient = new QueryClient(DEFAULT_QUERYCLIENT_OPTIONS);
const dehydratedState = window.__REACT_QUERY_STATE__;

const genericDynamicImportErrorHandler = (err: any) => {
    if (process.env.NODE_ENV !== 'production') {
        throw err;
    }
    return null;
};

const parentLocale = getParentLocale(
    getLocaleOverride() || props.env.localeInfo.locale,
);

const errorMessage = () => {
    return (
        <div
            style={{
                display: 'flex',
                height: '85vh',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <p>{gettext('An error has occurred.')}</p>
            <p>{gettext('Please try refreshing the page.')}</p>
        </div>
    );
};

const main = async () => {
    const initializeValues =
        props?.statsig?.initial_values?.bootstrap?.initialize_values;
    const initializedStableId =
        initializeValues?.evaluated_keys?.customIDs?.stableID;
    const clientKey = initializeValues
        ? STATSIG_SERVER_BOOTSTRAP_CLIENT_KEY
        : undefined;

    try {
        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations:
                    /* webpackChunkName: "discover-i18n-translation" */ import(
                        `./i18n/translations/${parentLocale}.json`
                    ),
            });
        }
    } catch (e) {
        logger.error(e, props as any);
    }

    try {
        await initializeSdk({
            ...props,
            ...(initializeValues ? { initializeValues } : {}),
            clientKey,
            options: {
                evaluationCallback: rumStatsigEvaluationCallback,
                overrideStableID: initializedStableId,
            },
        });
    } catch (e) {
        logger.error(e, props as any);
    }

    loadableReady(() => {
        //We are not performing any pre-fetch calls
        //on the server render. So this should
        //provide a safe fallback that keeps
        //"default" behavior if an asset is missing
        //from the window.

        //Additionally in the homepage case,
        //due to how we include assets via ESI tags,
        //it will share the dehydratedState across
        //requests regardless. This ensures we default
        //to a clean query cache on each load
        const safeDehydratedState = dehydratedState
            ? dehydratedState
            : dehydrate(queryClient);

        const locale = window?.__SERVER_DATA__?.env?.localeInfo?.locale;

        hydrate(
            <SentryErrorBoundary fallback={errorMessage}>
                <ConditionalStatsigSynchronousProvider
                    initializeValues={initializeValues}
                    user={props.user}
                    context={props}
                >
                    <TrackingContext.Provider value={{ locale }}>
                        <FeatureFlagsProvider
                            value={{
                                ...featureFlags,
                                ...eventCreationFeatureFlags,
                            }}
                        >
                            <QueryClientProvider client={queryClient}>
                                <Hydrate state={safeDehydratedState}>
                                    <App
                                        {...props}
                                        history={createHistory()}
                                        originalContext={props}
                                    />
                                </Hydrate>
                            </QueryClientProvider>
                        </FeatureFlagsProvider>
                    </TrackingContext.Provider>
                </ConditionalStatsigSynchronousProvider>
            </SentryErrorBoundary>,
            document.getElementById('root'),
        );
    });
};

main();

// Split out to keep initial bundle small
import('@eventbrite/site-analytics')
    .then(({ initDimensionsFromState }) => {
        // set up initial GA tracking
        initDimensionsFromState(props);
    })
    .catch(genericDynamicImportErrorHandler);
